import * as React from "react";
import { Modal, Grid, TextField, Box, Typography, Fade } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useLogin } from "./hooks/useLogin";
import { useStyles } from "./styles/useStyles";

interface Props {
  csrfToken: string;
  logo_src: string;
  button_src: string;
}

const SetInvitationPassword: React.FC<Props> = (props: any) => {
  const { classes } = useStyles();
  const { open, closeModal } = useLogin();
  const [password, setPassword] = React.useState<string>("");
  const [confirmPassword, setConfirmPassword] = React.useState<string>("");
  const [confirmPasswordError, setConfirmPasswordError] = React.useState("");

  const [rules, setRules] = React.useState({
    minLength: false,
    letter: false,
    number: false,
    specialChar: false,
  });

  const getParameter = (name) => {
    var url_string = window.location.href;
    var url = new URL(url_string);
    return url.searchParams.get(name);
  };

  const passwordRules = (pwd: string) => ({
    minLength: pwd.length >= 6,
    letter: /[a-zA-Z]/.test(pwd),
    number: /\d/.test(pwd),
    specialChar: /[^a-zA-Z0-9]/.test(pwd),
  });

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPwd = event.target.value;
    setPassword(newPwd);

    const r = passwordRules(newPwd);
    setRules(r);

    if (confirmPassword) {
      setConfirmPasswordError(
        newPwd === confirmPassword ? "" : "Passwords do not match"
      );
    }
  };

  const handleConfirmPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newConfirm = event.target.value;
    setConfirmPassword(newConfirm);

    setConfirmPasswordError(
      newConfirm === password ? "" : "Passwords do not match"
    );
  };

  const isFormValid =
    rules.minLength &&
    rules.letter &&
    rules.number &&
    rules.specialChar &&
    password === confirmPassword &&
    password.length > 0;

  return (
    <Modal
      open={open}
      onClose={() => {}}
      BackdropProps={{
        style: {
          opacity: "1",
          background: "#F2F2F2",
        },
      }}
    >
      <div className={classes.paper}>
        <div className={classes.paperInner}>
          <a href="/">
            <img
              src={props.logo_src}
              alt="Project Starfield"
              className={classes.loginLogo}
            />
          </a>

          <form
            id="setinvitationpassword"
            action="/users/invitation"
            method="post"
          >
            <input type="hidden" name="_method" value="put" />
            <input
              type="hidden"
              value={getParameter("invitation_token")}
              name="user[invitation_token]"
              id="user_invitation_token"
            />

            <input
              type="hidden"
              name="authenticity_token"
              value={props.csrfToken}
            />

            <TextField
              id="password"
              className={classes.textInput}
              required
              value={password}
              label="Password"
              name="user[password]"
              onChange={handlePasswordChange}
              variant="filled"
              type="password"
            />

            <Box sx={{ mt: 1, mb: 2, pl: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem", mb: 1, display: "block" }}
              >
                Password requirements:
              </Typography>

              <PasswordRule
                valid={rules.minLength}
                label="At least 6 characters"
              />
              <PasswordRule
                valid={rules.letter}
                label="At least one letter (a-z, A-Z)"
              />
              <PasswordRule
                valid={rules.number}
                label="At least one number (0-9)"
              />
              <PasswordRule
                valid={rules.specialChar}
                label="At least one special character (! $ @ % #)"
              />

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.7rem",
                  display: "block",
                  mt: 1,
                  fontStyle: "italic",
                }}
              >
                • Cannot reuse a recent password
              </Typography>
            </Box>

            <TextField
              id="confirm_password"
              className={classes.textInput}
              required
              value={confirmPassword}
              label="Password Confirmation"
              name="user[password_confirmation]"
              onChange={handleConfirmPasswordChange}
              variant="filled"
              type="password"
              error={!!confirmPasswordError}
              helperText={confirmPasswordError}
            />

            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
            >
              <Grid item xs={10}>
                <p className="loginText">Set your password</p>
              </Grid>
              <Grid item xs={2}>
                <button
                  type="submit"
                  form="setinvitationpassword"
                  value="Submit"
                  className={classes.clearButtonStyling}
                  disabled={!isFormValid}
                  style={{
                    opacity: isFormValid ? 1 : 0.5,
                    pointerEvents: isFormValid ? "auto" : "none",
                  }}
                >
                  <img
                    src={props.button_src}
                    alt="Set Password"
                    className={classes.loginButton}
                  />
                </button>
              </Grid>
            </Grid>
          </form>
        </div>
      </div>
    </Modal>
  );
};

const PasswordRule = ({ label, valid }) => (
  <Fade in timeout={300}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
      {valid ? (
        <CheckCircleIcon sx={{ color: "green", fontSize: "16px", mr: 1 }} />
      ) : (
        <RadioButtonUncheckedIcon
          sx={{ color: "#aaa", fontSize: "16px", mr: 1 }}
        />
      )}

      <Typography
        variant="caption"
        sx={{
          fontSize: "0.7rem",
          color: valid ? "green" : "text.secondary",
          textDecoration: valid ? "line-through" : "none",
        }}
      >
        {label}
      </Typography>
    </Box>
  </Fade>
);

export default SetInvitationPassword;
